import React from "react";
import ProjectLanguages from "components/projectLanguages/ProjectLanguages";
import ProjectLinks from "components/ProjectLinks/ProjectLinks";
import { FadeIn } from "components/shared/FadeIn";
import type { Project } from "types";

interface ProjectCardProps {
  repo: Project;
  featured?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  repo,
  featured = false,
}) => {
  const hasLinks = (repo.links?.length || 0) > 0;
  const hasLanguages = (repo.languages?.length || 0) > 0;

  return (
    <FadeIn duration={800} distance="24px">
      <article
        className={`project-card-shell group relative h-full overflow-hidden rounded-lg border border-border transition-all duration-300 hover:border-border-hover hover:shadow-card-hover ${
          featured ? "p-6 md:min-h-[360px] md:p-8" : "p-6 md:p-7"
        }`}
      >
        <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-accent via-[var(--accent-cool)] to-transparent" />
        <div
          className={`relative z-10 flex h-full flex-col ${
            featured ? "md:grid md:grid-cols-[1.1fr_0.9fr] md:gap-8" : ""
          }`}
        >
          <div className="flex flex-col">
            <div className="flex items-start justify-between gap-4 mb-5">
              <span className="inline-flex items-center gap-1.5 rounded-md border border-accent/20 bg-accent-dim px-2.5 py-1 text-[10px] font-mono tracking-[0.12em] uppercase text-accent">
                <span className="w-1 h-1 rounded-full bg-accent" />
                {featured ? "Featured Build" : "Project"}
              </span>
              {hasLinks && (
                <span className="rounded-md border border-border bg-bg-surface/80 px-2.5 py-1 text-[10px] font-mono tracking-wider uppercase text-text-muted">
                  {repo.links?.length} link
                  {repo.links?.length === 1 ? "" : "s"}
                </span>
              )}
            </div>

            <h3
              className={`font-display font-bold tracking-normal text-text-primary transition-colors duration-200 group-hover:text-accent ${
                featured ? "text-2xl md:text-4xl" : "text-xl md:text-2xl"
              }`}
            >
              {repo.name}
            </h3>

            <p
              className={`mt-4 leading-relaxed text-text-secondary ${
                featured ? "text-sm md:text-base" : "text-sm"
              }`}
            >
              {repo.description}
            </p>
          </div>

          <div
            className={`flex flex-col ${
              featured
                ? "mt-7 md:mt-0 md:border-l md:border-border md:pl-8"
                : "mt-7"
            }`}
          >
            {hasLanguages && (
              <div className="mb-6">
                <p className="mb-3 text-[10px] font-mono tracking-[0.12em] uppercase text-text-muted">
                  Stack
                </p>
                <div className="flex flex-row content-start">
                  <ProjectLanguages logos={repo.languages || []} />
                </div>
              </div>
            )}

            <div className="mt-auto grid grid-cols-2 gap-3 border-y border-border py-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-text-muted">
                  Status
                </p>
                <p className="mt-1 text-sm font-semibold text-text-primary">
                  Production-ready
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-text-muted">
                  Stack Size
                </p>
                <p className="mt-1 text-sm font-semibold text-text-primary">
                  {repo.languages?.length ?? 0} tools
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 pt-5">
              <span className="text-xs font-mono text-text-muted">
                Open details
              </span>
              <div className="flex flex-row items-end">
                <ProjectLinks logos={repo.links || []} />
              </div>
            </div>
          </div>
        </div>
      </article>
    </FadeIn>
  );
};

export default ProjectCard;
